/**
 * NUMERAÇÃO DOCUMENTAL — modelo configurável (CONFIGURAÇÕES → NUMERAÇÃO).
 *
 * Funções puras: validar um modelo, montar o número a partir dos tokens e
 * dizer em qual "escopo de sequência" ele conta. A sequência em si (o número
 * que sobe) NÃO mora aqui: é alocada de forma atômica pelo repositório
 * (`SystemSettingsRepository.nextSequence`), nunca por `MAX(numero) + 1` nem
 * por `lista.length + 1`, que duplicam quando duas pessoas criam ao mesmo
 * tempo.
 *
 * Os modelos PADRÃO reproduzem EXATAMENTE o formato que cada módulo já
 * gerava antes desta tela existir. Nada muda de número enquanto ninguém
 * alterar a configuração — e mesmo depois, só documentos NOVOS recebem o
 * formato novo: o número é atribuído uma única vez, na criação.
 */

export type NumberedDocumentType = "RI" | "ME" | "LP" | "PM" | "USC" | "BSCAN" | "AVR" | "MINUTA";

export const NUMBERED_DOCUMENT_TYPES: readonly NumberedDocumentType[] = ["RI", "ME", "LP", "PM", "USC", "BSCAN", "AVR", "MINUTA"];

export const NUMBERED_DOCUMENT_LABELS: Record<NumberedDocumentType, string> = {
  RI: "Relatório de Integridade (RI)",
  ME: "Medição de Espessura (ME)",
  LP: "Líquido Penetrante (LP)",
  PM: "Partículas Magnéticas (PM)",
  USC: "Ultrassom Convencional (US)",
  BSCAN: "B-Scan / C-Scan",
  AVR: "AVR — Avaliação de Vida Remanescente",
  MINUTA: "Minuta de Recomendações"
};

/** Valor de {TIPO} por documento — o prefixo que cada módulo já usava. */
export const DOCUMENT_TYPE_TOKEN: Record<NumberedDocumentType, string> = {
  RI: "RI", ME: "ME", LP: "LP", PM: "PM", USC: "USC", BSCAN: "BSCAN", AVR: "AVR", MINUTA: "MIN"
};

export interface NumberingTokenInfo {
  readonly token: string;
  readonly label: string;
  readonly example: string;
  /** Tokens fora da lista do comando, existentes só para reproduzir o formato histórico. */
  readonly legacy?: boolean;
}

export const NUMBERING_TOKENS: readonly NumberingTokenInfo[] = [
  { token: "{TIPO}", label: "Tipo do documento", example: "RI" },
  { token: "{CLIENTE}", label: "Código do Cliente", example: "PRE" },
  { token: "{PROJETO}", label: "Código do Projeto", example: "PRE2026" },
  { token: "{UNIDADE}", label: "Código da Unidade / Área", example: "U33" },
  { token: "{ANO}", label: "Ano (4 dígitos)", example: "2026" },
  { token: "{MES}", label: "Mês (2 dígitos)", example: "09" },
  { token: "{SEQUENCIAL}", label: "Sequencial (com os dígitos configurados)", example: "000123" },
  { token: "{REVISAO}", label: "Revisão na criação", example: "00" },
  { token: "{OS}", label: "Nº da OS (só dígitos)", example: "001" },
  { token: "{SUBOS}", label: "Nº da Sub-OS (só dígitos)", example: "01" },
  { token: "{CLIENTE_LEGADO}", label: "3 primeiros caracteres do identificador interno do Cliente — formato histórico do RI; prefira {CLIENTE}", example: "CLI", legacy: true },
  { token: "{PROJETO_LEGADO}", label: "3 primeiros caracteres do identificador interno do Projeto — formato histórico da Minuta; prefira {PROJETO}", example: "PRJ", legacy: true }
];

const KNOWN_TOKENS = new Set(NUMBERING_TOKENS.map(t => t.token));

export interface NumberingConfig {
  readonly documentType: NumberedDocumentType;
  readonly template: string;
  readonly digits: number;
  readonly prefix: string;
  readonly suffix: string;
  /** Caractere que o montador de modelo insere entre os tokens escolhidos. */
  readonly separator: string;
  readonly resetAnnual: boolean;
  readonly resetPerClient: boolean;
  readonly resetPerProject: boolean;
}

const base = (documentType: NumberedDocumentType, template: string, digits: number, resets: Pick<NumberingConfig, "resetAnnual" | "resetPerClient" | "resetPerProject">, separator = "-"): NumberingConfig =>
  ({ documentType, template, digits, prefix: "", suffix: "", separator, ...resets });

/**
 * Formatos históricos, um por módulo:
 *  - END e AVR: `PREFIXO-AAAA-NNNN`, contando por Cliente dentro do ano;
 *  - RI: `RI_<3 do id do Cliente>_<OS>_<SubOS>_AAAA_NN`, contando por Cliente (todos os anos);
 *  - Minuta: `MIN-<3 do id do Projeto>-NNNNNN-AAAA`, contando por Projeto dentro do ano.
 */
export const DEFAULT_NUMBERING: Record<NumberedDocumentType, NumberingConfig> = {
  RI: base("RI", "{TIPO}_{CLIENTE_LEGADO}_{OS}_{SUBOS}_{ANO}_{SEQUENCIAL}", 2, { resetAnnual: false, resetPerClient: true, resetPerProject: false }, "_"),
  ME: base("ME", "{TIPO}-{ANO}-{SEQUENCIAL}", 4, { resetAnnual: true, resetPerClient: true, resetPerProject: false }),
  LP: base("LP", "{TIPO}-{ANO}-{SEQUENCIAL}", 4, { resetAnnual: true, resetPerClient: true, resetPerProject: false }),
  PM: base("PM", "{TIPO}-{ANO}-{SEQUENCIAL}", 4, { resetAnnual: true, resetPerClient: true, resetPerProject: false }),
  USC: base("USC", "{TIPO}-{ANO}-{SEQUENCIAL}", 4, { resetAnnual: true, resetPerClient: true, resetPerProject: false }),
  BSCAN: base("BSCAN", "{TIPO}-{ANO}-{SEQUENCIAL}", 4, { resetAnnual: true, resetPerClient: true, resetPerProject: false }),
  AVR: base("AVR", "{TIPO}-{ANO}-{SEQUENCIAL}", 4, { resetAnnual: true, resetPerClient: true, resetPerProject: false }),
  MINUTA: base("MINUTA", "{TIPO}-{PROJETO_LEGADO}-{SEQUENCIAL}-{ANO}", 6, { resetAnnual: true, resetPerClient: false, resetPerProject: true })
};

/**
 * Documentos listados no comando que NÃO têm uma sequência para configurar.
 * Aparecem na tela com o motivo — uma caixa de configuração que não muda
 * nada seria um controle que mente.
 */
export const NON_CONFIGURABLE_NUMBERING: ReadonlyArray<{ documentType: string; label: string; currentFormat: string; reason: string }> = [
  { documentType: "RDO", label: "RDO", currentFormat: "RDO-AAAAMMDD-XXXX", reason: "O número do RDO é derivado da data e do próprio registro, não de uma sequência." },
  { documentType: "BOOK", label: "Book de Inspeção", currentFormat: "BOOK-AAAA-<Inspeção>", reason: "O número do Book é derivado da Inspeção que ele consolida — um Book por Inspeção." },
  { documentType: "INSPECAO", label: "Inspeção", currentFormat: "—", reason: "A Inspeção não recebe número documental próprio; é identificada pelo Equipamento, pela OS e pela data." },
  { documentType: "RECOMENDACAO", label: "Recomendação", currentFormat: "ZR atribuído pela Engenharia", reason: "A numeração ZR é ato de Engenharia (permissão própria), não uma sequência automática." }
];

export interface NumberingTokenValues {
  readonly year: number;
  readonly month: number;
  readonly revision?: string | null;
  readonly clientId?: string | null;
  readonly clientCode?: string | null;
  readonly projectId?: string | null;
  readonly projectCode?: string | null;
  readonly unitCode?: string | null;
  readonly osNumber?: string | null;
  readonly subOsNumber?: string | null;
}

export function tokensOf(template: string): string[] {
  return template.match(/\{[A-Z_]+\}/g) ?? [];
}

/** Erros que impedem gravar o modelo. */
export function validateNumberingConfig(config: NumberingConfig): string[] {
  const errors: string[] = [];
  const label = NUMBERED_DOCUMENT_LABELS[config.documentType] ?? config.documentType;
  const template = (config.template ?? "").trim();
  if (!template) return [`${label}: o modelo de numeração não pode ficar vazio.`];
  if (template.length > 80) errors.push(`${label}: o modelo tem mais de 80 caracteres.`);
  const tokens = tokensOf(template);
  for (const token of tokens) if (!KNOWN_TOKENS.has(token)) errors.push(`${label}: o token ${token} não existe.`);
  const count = (token: string) => tokens.filter(t => t === token).length;
  if (count("{SEQUENCIAL}") !== 1) errors.push(`${label}: o modelo precisa ter {SEQUENCIAL} exatamente uma vez — sem ele dois documentos teriam o mesmo número.`);
  // Reiniciar a contagem sem o token que distingue o escopo repetiria números.
  if (config.resetAnnual && count("{ANO}") === 0) errors.push(`${label}: com reinício anual o modelo precisa ter {ANO}; sem ele o número 1 do ano novo repetiria o do ano anterior.`);
  if (config.resetPerProject && count("{PROJETO}") === 0 && count("{PROJETO_LEGADO}") === 0) errors.push(`${label}: com reinício por Projeto o modelo precisa ter {PROJETO}; sem ele Projetos diferentes gerariam o mesmo número.`);
  if (!Number.isInteger(config.digits) || config.digits < 1 || config.digits > 10) errors.push(`${label}: a quantidade de dígitos deve ficar entre 1 e 10.`);
  const literal = /^[A-Za-z0-9 _\-./]*$/;
  if (!literal.test(config.prefix ?? "") || (config.prefix ?? "").length > 20) errors.push(`${label}: o prefixo aceita só letras, números e _ - . / (até 20).`);
  if (!literal.test(config.suffix ?? "") || (config.suffix ?? "").length > 20) errors.push(`${label}: o sufixo aceita só letras, números e _ - . / (até 20).`);
  if (!/^[_\-./]?$/.test(config.separator ?? "")) errors.push(`${label}: o separador deve ser um de _ - . / ou vazio.`);
  if (!literal.test(template.replace(/\{[A-Z_]+\}/g, ""))) errors.push(`${label}: fora dos tokens, o modelo aceita só letras, números e _ - . /`);
  return errors;
}

/** Avisos que não impedem gravar, mas a pessoa precisa saber. */
export function numberingWarnings(config: NumberingConfig): string[] {
  const warnings: string[] = [];
  const tokens = tokensOf(config.template);
  if (config.resetPerClient && !tokens.includes("{CLIENTE}") && !tokens.includes("{CLIENTE_LEGADO}")) {
    warnings.push("A contagem é por Cliente e o modelo não mostra o Cliente: dois Clientes podem ter documentos com o mesmo número (é o comportamento histórico — cada número é único dentro do seu Cliente).");
  }
  if (tokens.includes("{REVISAO}")) warnings.push("O número é atribuído uma única vez, na criação: {REVISAO} sai sempre com a revisão inicial e não acompanha revisões posteriores.");
  if (tokens.includes("{CLIENTE_LEGADO}") || tokens.includes("{PROJETO_LEGADO}")) warnings.push("O modelo usa um token de formato histórico (identificador interno). Prefira {CLIENTE}/{PROJETO}, que usam o código cadastrado.");
  return warnings;
}

/** Valor de token seguro para número de documento: sem espaço, sem caractere que quebre nome de arquivo. */
export function sanitizeTokenValue(value: string | null | undefined): string {
  return String(value ?? "").trim().toUpperCase().replace(/\s+/g, "-").replace(/[^A-Z0-9_\-./]/g, "");
}

export function buildTemplateFromTokens(tokens: readonly string[], separator: string): string {
  return tokens.filter(t => KNOWN_TOKENS.has(t)).join(separator);
}

export class NumberingTokenMissingError extends Error {
  constructor(readonly token: string, readonly documentType: NumberedDocumentType) {
    super(`${NUMBERED_DOCUMENT_LABELS[documentType]}: o modelo de numeração usa ${token}, e este documento não tem esse dado. Complete o cadastro ou ajuste o modelo em Configurações → Numeração.`);
  }
}

/**
 * Monta o número. Token sem valor RECUSA a numeração em vez de imprimir um
 * buraco ("RI__001") — um número de documento incompleto é pior que um erro
 * que diz qual cadastro falta.
 */
export function renderDocumentNumber(config: NumberingConfig, values: NumberingTokenValues, sequence: number): string {
  const require = (token: string, raw: string | null | undefined): string => {
    const clean = sanitizeTokenValue(raw);
    if (!clean) throw new NumberingTokenMissingError(token, config.documentType);
    return clean;
  };
  const digitsOnly = (raw: string | null | undefined) => String(raw ?? "").replace(/\D/g, "");
  const body = config.template.replace(/\{[A-Z_]+\}/g, token => {
    switch (token) {
      case "{TIPO}": return DOCUMENT_TYPE_TOKEN[config.documentType];
      case "{CLIENTE}": return require(token, values.clientCode);
      case "{PROJETO}": return require(token, values.projectCode);
      case "{UNIDADE}": return require(token, values.unitCode);
      case "{ANO}": return String(values.year);
      case "{MES}": return String(values.month).padStart(2, "0");
      case "{SEQUENCIAL}": return String(sequence).padStart(config.digits, "0");
      case "{REVISAO}": return require(token, values.revision ?? "00");
      // Formato histórico do RI: OS/Sub-OS só com dígitos, com o padrão que o módulo já usava.
      case "{OS}": return digitsOnly(values.osNumber) || "001";
      case "{SUBOS}": return digitsOnly(values.subOsNumber) || "01";
      case "{CLIENTE_LEGADO}": return (values.clientId || "IND").substring(0, 3).toUpperCase();
      case "{PROJETO_LEGADO}": return (values.projectId || "").substring(0, 3).toUpperCase() || "PRJ";
      default: return token;
    }
  });
  return `${config.prefix ?? ""}${body}${config.suffix ?? ""}`;
}

/**
 * Chave do contador. Dois documentos só disputam a mesma sequência quando
 * caem no mesmo escopo (tipo + ano, Cliente, Projeto conforme os reinícios).
 * O modelo entra na chave: trocar o formato começa uma contagem nova em vez
 * de continuar a de um formato que já não é impresso.
 */
export function sequenceScopeKey(config: NumberingConfig, values: NumberingTokenValues): string {
  const parts = [config.documentType, `T:${config.prefix}|${config.template}|${config.suffix}`];
  if (config.resetAnnual) parts.push(`Y:${values.year}`);
  if (config.resetPerClient) parts.push(`C:${values.clientId ?? "-"}`);
  if (config.resetPerProject) parts.push(`P:${values.projectId ?? "-"}`);
  return parts.join("|");
}

export function previewDocumentNumber(config: NumberingConfig, now = new Date()): string {
  return renderDocumentNumber(config, {
    year: now.getFullYear(), month: now.getMonth() + 1, revision: "00",
    clientId: "client-exemplo", clientCode: "PRE", projectId: "prj-exemplo", projectCode: "PRE2026",
    unitCode: "U33", osNumber: "001", subOsNumber: "01"
  }, 123);
}
